/**
 * <TutorStudentsPage> — l'onglet « Mes élèves » de l'espace tuteur (page).
 *
 * `MyStudentsTabScreen.js`, rhabillé : le titre et ses deux gestes — « Activité », qui ouvre « Générer
 * une activité » pour plusieurs élèves à la fois (seulement quand le tuteur a des élèves), et la
 * relecture de la liste — puis ses élèves, un par ligne (`<TutorStudentRow>`), dans UNE carte-liste.
 * Aucun élève : « Aucun élève ». L'ancien écran n'avait ni filtre ni recherche : la page n'en invente pas.
 *
 * Toucher un élève ouvre sa fiche, poussée ; ses non-lus, sa conversation — les élèves qui en ont sont les premiers
 * de la liste (l'écran les range en tête) ; au large, « Mes élèves » est le cadre du live
 * (`<TutorStudentsDeskPage>`), qui pose ses lignes resserrées dans sa colonne de gauche.
 *
 * Contrôlée : les élèves, le chargement, la relecture et l'erreur arrivent en props ; chaque geste
 * remonte avec son élève. « Générer une activité » est une surface à part, que la page ne monte pas.
 */
import React from "react"

import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Title } from "../../atoms/typography/Title"
import { CardList } from "../../molecules/CardList"
import { EmptyState } from "../../molecules/EmptyState"
import { RefreshButton } from "../../molecules/RefreshButton"
import { TutorStudentRow } from "../../organisms/TutorStudentRow"
import { PageTemplate, PARENT_CONTENT_WIDTH } from "../../templates/PageTemplate"
import { type UITutorStudent } from "../../../ports/primary/ui/types/tutorEleves"
import { TutorStudentsSkeleton } from "./TutorStudentsSkeleton"

/** L'activité commune, dite à un lecteur d'écran : la même dans « Mes élèves » et dans son cadre du live. */
export const GENERATE_FOR_STUDENTS_HINT = "Générer une activité pour plusieurs élèves"

const TEXTS = {
    title: "Mes élèves",
    generate: "Activité",
    generateHint: GENERATE_FOR_STUDENTS_HINT,
    empty: "Aucun élève",
}

export type TutorStudentsPageProps = {
    students: UITutorStudent[]
    onOpenProfile?: (student: UITutorStudent) => void
    /** Sa bulle touchée, ses non-lus dedans : sa conversation (la fiche, sur Messages). */
    onOpenMessages?: (student: UITutorStudent) => void
    /** « Activité » — générer une activité commune. Monté seulement quand il y a des élèves. */
    onGenerateForStudents?: () => void

    refreshing?: boolean
    onRefresh?: () => void
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    testID?: string
}

export function TutorStudentsPage(props: TutorStudentsPageProps) {
    const {
        students,
        onOpenProfile,
        onOpenMessages,
        onGenerateForStudents,
        refreshing = false,
        onRefresh,
        loading = false,
        error,
        onRetry,
        testID,
    } = props

    return (
        <PageTemplate
            refreshing={refreshing}
            onRefresh={onRefresh}
            testID={testID}
            loading={loading}
            skeleton={<TutorStudentsSkeleton />}
            error={error}
            onRetry={onRetry}
            padding="4"
            maxContentWidth={PARENT_CONTENT_WIDTH}
        >
            <Stack gap="4">
                <Row justify="between" align="center">
                    <Title size="text-2xl">{TEXTS.title}</Title>
                    <Row gap="1" align="center">
                        {students.length > 0 && onGenerateForStudents != null && (
                            <Button
                                label={TEXTS.generate}
                                iconName="plus"
                                size="sm"
                                onPress={onGenerateForStudents}
                                accessibilityHint={TEXTS.generateHint}
                            />
                        )}
                        {onRefresh != null && <RefreshButton refreshing={refreshing} onRefresh={onRefresh} />}
                    </Row>
                </Row>

                {students.length === 0 ? (
                    <EmptyState iconName="user-graduate" title={TEXTS.empty} />
                ) : (
                    <CardList>
                        {students.map((student) => (
                            <TutorStudentRow
                                key={student.id}
                                student={student}
                                onOpenProfile={onOpenProfile != null ? () => onOpenProfile(student) : undefined}
                                onOpenMessages={onOpenMessages != null ? () => onOpenMessages(student) : undefined}
                                testID={`tutor-student-${student.id}`}
                            />
                        ))}
                    </CardList>
                )}
            </Stack>
        </PageTemplate>
    )
}
